import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  type ChartConfig,
} from '@/components/ui/chart'
import { Typography } from '@/components/ui/typography'
import type { CompanyMetricReview } from '@/features/actual-vs-budget/company-review'
import { formatUsdMillions } from '@/features/actual-vs-budget/format'

const chartConfig = {
  actual: { label: 'Actual', color: '#243b5a' },
  budget: { label: 'Budget', color: '#5f6e85' },
  forecast: { label: 'Forecast', color: '#bd6448' },
} satisfies ChartConfig

export function CompanyTrajectoryChart({
  metric,
}: {
  metric: CompanyMetricReview
}) {
  const latestClosed = metric.trajectory.find(({ isLatestClosed }) =>
    Boolean(isLatestClosed)
  )
  const hasPhasedBudget = metric.trajectory.some(
    ({ budget }) => budget !== null
  )
  const hasPhasedForecast = metric.trajectory.some(
    ({ forecast }) => forecast !== null
  )
  const hasTrajectoryData = metric.trajectory.some(
    ({ actual, budget, forecast }) =>
      actual !== null || budget !== null || forecast !== null
  )
  const annualBudget =
    metric.budgetAvailability === 'annual-only'
      ? metric.summary.fullYearBudget
      : null
  const hasChartData = hasTrajectoryData || annualBudget !== null
  const chartData = metric.trajectory.map((point, index) => ({
    ...point,
    annualBudgetMarker:
      index === metric.trajectory.length - 1 ? annualBudget : null,
    annualBudgetLabel:
      index === metric.trajectory.length - 1 && annualBudget !== null
        ? `Annual Budget · ${formatUsdMillions(annualBudget)} · right axis`
        : null,
  }))

  return (
    <section aria-labelledby="company-trajectory-heading" className="space-y-3">
      <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
        <div>
          <Typography
            id="company-trajectory-heading"
            as="h2"
            variant="h5"
            className="text-lg font-semibold"
          >
            Actuals, Budget, and Forecast trajectory
          </Typography>
          <Typography variant="muted" className="mt-1">
            {metric.label} across the company fiscal year. Actuals stop at the
            latest closed month; Forecast continues forward.
          </Typography>
        </div>
        <Typography variant="body-sm" className="text-primary/70">
          USD millions
        </Typography>
      </div>

      <div className="rounded-lg border border-primary/10 bg-white p-4">
        {hasChartData ? (
          <ChartContainer
            config={chartConfig}
            className="h-80 w-full aspect-auto [&_.recharts-tooltip-cursor]:stroke-primary/20"
          >
            <LineChart
              data={chartData}
              margin={{ top: 28, right: 130, bottom: 4, left: 4 }}
              title={`${metric.label} fiscal-year trajectory`}
            >
              <CartesianGrid
                vertical={false}
                stroke="color-mix(in srgb, var(--primary) 10%, transparent)"
              />
              <XAxis
                dataKey="monthLabel"
                axisLine={false}
                tickLine={false}
                tickMargin={10}
                tick={{
                  fill: 'color-mix(in srgb, var(--primary) 55%, transparent)',
                  fontSize: 11,
                }}
              />
              <YAxis
                yAxisId="monthly"
                axisLine={false}
                tickLine={false}
                width={56}
                tickFormatter={(value: number) => `$${value.toFixed(0)}M`}
                tick={{
                  fill: 'color-mix(in srgb, var(--primary) 55%, transparent)',
                  fontSize: 11,
                }}
              />
              {annualBudget !== null && (
                <YAxis
                  yAxisId="annual"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  width={62}
                  domain={[0, Math.ceil(annualBudget * 1.1)]}
                  tickCount={3}
                  tickFormatter={(value: number) => `$${value.toFixed(0)}M`}
                  tick={{
                    fill: 'var(--color-budget)',
                    fontSize: 11,
                  }}
                />
              )}
              <ChartTooltip
                formatter={(value, name) => [
                  formatUsdMillions(Number(value)),
                  String(name),
                ]}
                labelFormatter={(label) => `${String(label)} · fiscal month`}
                contentStyle={{
                  borderRadius: 6,
                  borderColor:
                    'color-mix(in srgb, var(--primary) 15%, transparent)',
                  boxShadow: '0 12px 28px rgb(15 23 42 / 0.12)',
                }}
              />
              <ChartLegend verticalAlign="top" height={32} />
              {latestClosed && (
                <ReferenceLine
                  yAxisId="monthly"
                  x={latestClosed.monthLabel}
                  stroke="color-mix(in srgb, var(--primary) 35%, transparent)"
                  strokeDasharray="2 4"
                  label={{
                    value: 'Latest closed month',
                    position: 'insideTopLeft',
                    fill: 'color-mix(in srgb, var(--primary) 60%, transparent)',
                    fontSize: 10,
                  }}
                />
              )}
              <Line
                yAxisId="monthly"
                type="monotone"
                dataKey="actual"
                name="Actual"
                stroke="var(--color-actual)"
                strokeWidth={3}
                dot={{ r: 3, fill: 'var(--color-actual)', strokeWidth: 0 }}
                activeDot={{ r: 5 }}
                connectNulls={false}
                isAnimationActive="auto"
              >
                <LabelList
                  dataKey="actualLabel"
                  position="bottom"
                  offset={10}
                  fill="var(--color-actual)"
                  fontSize={11}
                />
              </Line>
              <Line
                yAxisId="monthly"
                type="monotone"
                dataKey="budget"
                name="Budget"
                stroke="var(--color-budget)"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
                isAnimationActive="auto"
              >
                <LabelList
                  dataKey="budgetLabel"
                  position="top"
                  offset={10}
                  fill="var(--color-budget)"
                  fontSize={11}
                />
              </Line>
              <Line
                yAxisId="monthly"
                type="monotone"
                dataKey="forecast"
                name="Forecast"
                stroke="var(--color-forecast)"
                strokeWidth={2.5}
                strokeDasharray="7 5"
                dot={{
                  r: 3,
                  fill: 'white',
                  stroke: 'var(--color-forecast)',
                  strokeWidth: 2,
                }}
                activeDot={{ r: 5 }}
                connectNulls={false}
                isAnimationActive="auto"
              >
                <LabelList
                  dataKey="forecastLabel"
                  position="bottom"
                  offset={10}
                  fill="var(--color-forecast)"
                  fontSize={11}
                />
              </Line>
              {annualBudget !== null && (
                <Line
                  yAxisId="annual"
                  type="linear"
                  dataKey="annualBudgetMarker"
                  name="Annual Budget"
                  stroke="transparent"
                  dot={{
                    r: 5,
                    fill: 'var(--color-budget)',
                    stroke: 'white',
                    strokeWidth: 2,
                  }}
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                  legendType="none"
                  isAnimationActive="auto"
                >
                  <LabelList
                    dataKey="annualBudgetLabel"
                    position="top"
                    fill="var(--color-budget)"
                    fontSize={11}
                  />
                </Line>
              )}
            </LineChart>
          </ChartContainer>
        ) : (
          <div className="flex h-80 items-center justify-center rounded-md bg-muted/20 px-6 text-center">
            <div>
              <Typography className="font-medium text-primary">
                No matched trajectory data
              </Typography>
              <Typography variant="muted" className="mt-1 text-sm">
                Actual, Budget, and Forecast values are unavailable for these
                fiscal periods.
              </Typography>
            </div>
          </div>
        )}

        {(!hasPhasedBudget || !hasPhasedForecast) && (
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 border-t border-primary/8 pt-3">
            {!hasPhasedBudget && (
              <Typography variant="muted" className="text-xs">
                {metric.budgetAvailability === 'annual-only'
                  ? 'Monthly Budget phasing unavailable; Annual Budget uses the right axis and is shown above.'
                  : 'Budget data unavailable.'}
              </Typography>
            )}
            {!hasPhasedForecast && (
              <Typography variant="muted" className="text-xs">
                {metric.forecastAvailability === 'annual-only'
                  ? 'Monthly Forecast phasing unavailable; full-year Forecast is shown above.'
                  : 'Forecast unavailable.'}
              </Typography>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
